'use client';

// Third Party Plugins
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
// Types
import { PostCardProps } from './types';
// Utilities
import truncateText from '@utils/truncateText';
// Styles
import {
  StyledContentContainer,
  StyledFontAwesomeIcon,
  StyledPostCardContainer,
  StyledPostCardImgWrapper,
} from './styles';
import { StyledMetaContainer } from '@components/MetaContent/styles';
import {
  StyledTextContainer,
  StyledTextHeading,
} from '@components/TextContent/styles';
// Custom Components
import DefaultThumbnailImage from '@components/images/DefaultThumbnailImage';
import Tag from '@components/tags/Tag';

const PostCard: React.FC<PostCardProps> = ({
  type,
  to,
  image,
  altText,
  date,
  tagText,
  title,
  description,
  isOnHomePage,
}) => {
  return (
    <Link href={to} passHref legacyBehavior>
      <StyledPostCardContainer $type={type}>
        {type !== `tomsBlog` &&
          (image ? (
            <StyledPostCardImgWrapper>
              {image ? (
                <Image
                  src={image}
                  layout="fill"
                  objectFit="contain"
                  alt={altText || 'Default alt text'} // Provide a fallback alt text
                />
              ) : (
                <div>No image available</div> // Optionally render a fallback content
              )}
            </StyledPostCardImgWrapper>
          ) : (
            <DefaultThumbnailImage
              height={134}
              width={200}
              borderRadius="var(--radius-md)"
            />
          ))}
        <StyledContentContainer isOnHomePage={isOnHomePage}>
          {tagText && <MetaContent tag={tagText} date={date} />}
          <TextContent
            type={type}
            heading={title}
            excerpt={description}
            charLimitHeading={100}
            charLimitExcerpt={54}
            isOnHomePage={isOnHomePage}
          />
        </StyledContentContainer>
        <StyledFontAwesomeIcon
          className="minor-icon-right"
          icon={faAngleRight}
        />
      </StyledPostCardContainer>
    </Link>
  );
};

const MetaContent: React.FC<{ tag: string; date: Date | string }> = ({
  tag,
  date,
}) => {
  return (
    <StyledMetaContainer>
      <Tag type="primary" text={tag} color="var(--c-yellow-1)" />
      <time dateTime={new Date(date).toISOString()}>
        {typeof date === 'string' ? date : date.toLocaleDateString()}
      </time>
    </StyledMetaContainer>
  );
};

const TextContent: React.FC<{
  type: string;
  heading: string;
  excerpt?: string;
  charLimitHeading: number;
  charLimitExcerpt: number;
  isOnHomePage?: boolean;
}> = ({
  type,
  heading,
  excerpt,
  charLimitHeading,
  charLimitExcerpt,
  isOnHomePage,
}) => {
  switch (type) {
    case `news`:
      return (
        <StyledTextContainer>
          <StyledTextHeading title={heading}>
            {truncateText(charLimitHeading, heading)}
          </StyledTextHeading>
          {!isOnHomePage && (
            <p title={excerpt}>{truncateText(charLimitExcerpt, excerpt)}</p>
          )}
        </StyledTextContainer>
      );
    default:
      return (
        <StyledTextContainer $type={type}>
          <StyledTextHeading $type={type} title={heading}>
            {truncateText(charLimitHeading, heading)}
          </StyledTextHeading>
          <p title={excerpt}>{truncateText(charLimitExcerpt, excerpt)}</p>
        </StyledTextContainer>
      );
  }
};

export default PostCard;
